import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, FileWarning, Loader2, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

/**
 * The 110 Helpjuice assets that live on private S3 storage (403 to everyone
 * but Helpjuice) and therefore could not be imported. This is the chase list —
 * request these files from Helpjuice before the account is retired.
 */
export default function KBMissingAssets() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/kb/missing-assets").then((r) => setData(r.data)).catch(() => setData({ assets: [] })); }, []);

  if (!data) return <div className="flex h-72 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-300 dark:text-slate-600" /></div>;

  const csv = () => {
    const rows = [["url", "kind", "ext", "articles"], ...data.assets.map((a) => [a.url, a.kind, a.ext, (a.articles || []).map((x) => x.name).join(" | ")])];
    const blob = new Blob([rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url; link.download = "kb-missing-assets.csv"; link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="kb-missing-assets-page" className="kb-surface">
      <Button variant="outline" size="sm" onClick={() => navigate("/kb")} data-testid="kb-missing-back" className="mb-4">
        <ArrowLeft className="h-4 w-4" /> Back to Knowledge Base
      </Button>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 dark:text-slate-100">Attachments needing re-upload</h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-500 dark:text-slate-400">
            Helpjuice stored these on private S3 storage that rejects anonymous requests (HTTP 403), so the importer
            couldn&apos;t pull them. Ask Helpjuice for a full asset export while the account is still live, then re-upload.
          </p>
        </div>
        <Button variant="outline" onClick={csv} data-testid="kb-missing-export">Export CSV</Button>
      </div>

      <div className="mt-5 overflow-hidden rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
        <div className="flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 bg-amber-50 px-4 py-2.5 text-xs text-amber-900">
          <FileWarning className="h-4 w-4" /> {data.count} referenced attachment{data.count === 1 ? "" : "s"} affected
        </div>
        <div className="divide-y divide-slate-100 dark:divide-slate-800">
          {data.assets.map((a) => (
            <div key={a.url} className="px-4 py-3" data-testid={`kb-missing-row-${a.upload_id || a.url.slice(-12)}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-slate-500 dark:text-slate-400">
                  {a.ext || a.kind || "file"}
                </span>
                <span className="truncate text-xs font-medium text-slate-700 dark:text-slate-200" title={a.url}>
                  {decodeURIComponent(a.url.split(/[/%]2F|\//).pop() || "").slice(0, 70)}
                </span>
                <span className="text-xs text-slate-400 dark:text-slate-500">{a.article_count} article{a.article_count === 1 ? "" : "s"}</span>
                <a href={a.url} target="_blank" rel="noreferrer"
                  className="flex items-center gap-1 text-xs text-primary hover:underline">
                  source URL <ExternalLink className="h-3 w-3" />
                </a>
              </div>
              <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
                {(a.articles || []).map((art) => (
                  <button key={art.article_id} onClick={() => navigate(`/kb/a/${art.slug}`)}
                    className="text-xs text-slate-600 dark:text-slate-300 hover:text-primary hover:underline">{art.name}</button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
